import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import fallbackHero from '../../../images/hero.jpg';
import useBreakpoint from '../../../hooks/useBreakpoint';
import { API_BASE_URL, FILE_BASE_URL } from '../../../apiConfig';

export default function Hero() {
    const [slides, setSlides] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const timerRef = useRef(null);
    const { isMobile, isTablet, isDesktop } = useBreakpoint();

    useEffect(() => {
        let isMounted = true;
        const langEl = document.getElementById('language');
        const language = langEl ? langEl.value : '1';
        
        axios.get(`${API_BASE_URL}/banners/${language}`)
            .then((res) => {
                if (isMounted && res.data.banners && res.data.banners.length > 0) {
                    setSlides(res.data.banners);
                }
            })
            .catch(() => {});
            
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        if (slides.length > 0) {
            const total = slides.length;
            timerRef.current = setInterval(() => {
                setCurrentIndex(prev => (prev + 1) % total);
            }, 5000);
            return () => clearInterval(timerRef.current);
        }
    }, [slides]);

    const goTo = (index) => {
        if (slides.length === 0) return;
        const total = slides.length;
        setCurrentIndex((index + total) % total);
    };

    // Responsive values
    const navHeight     = isMobile ? 70 : isTablet ? 100 : 112;
    const controlHeight = isMobile ? 56 : 60;   // arrow (36px) + gap
    const controlBottom = isMobile ? 20 : 36;
    // Content must clear the controls: controlBottom + controlHeight + extra gap
    const contentPadB   = isMobile ? `${controlHeight + controlBottom + 20}px`
                        : isTablet ? `${controlHeight + controlBottom + 28}px`
                        :            `${controlHeight + controlBottom + 36}px`;

    return (
        <section style={{
            position: 'relative',
            width: '100%',
            height: `calc(100vh - ${navHeight}px)`,
            minHeight: isMobile ? '380px' : '500px',
            maxHeight: '820px',
            overflow: 'hidden',
            background: '#0a0c3a',
        }}>
            {/* Slides */}
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                {slides.map((slide, index) => (
                    <div
                        key={index}
                        style={{
                            position: 'absolute', inset: 0,
                            opacity: index === currentIndex ? 1 : 0,
                            transition: 'opacity 0.8s ease',
                            zIndex: index === currentIndex ? 1 : 0,
                        }}
                    >
                        {/* Background */}
                        <div style={{
                            position: 'absolute', inset: 0,
                            backgroundImage: `url(${slide.image ? `${FILE_BASE_URL}/${slide.image}` : fallbackHero})`,
                            backgroundSize: 'cover',
                            backgroundPosition: isMobile ? 'center center' : 'center top',
                            backgroundRepeat: 'no-repeat',
                            transform: index === currentIndex ? 'scale(1)' : 'scale(1.04)',
                            transition: 'transform 6s ease',
                        }} />

                        {/* Overlay - only show if there is content to display */}
                        {(slide.heading || slide.sub_text || slide.link) && (
                            <>
                                <div style={{
                                    position: 'absolute', inset: 0,
                                    background: isMobile
                                        ? 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.75) 100%)'
                                        : 'linear-gradient(to right, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,0.1) 100%)',
                                    zIndex: 2,
                                }} />

                                {/* Content */}
                                <div style={{
                                    position: 'absolute', inset: 0, zIndex: 3,
                                    display: 'flex',
                                    alignItems: isMobile ? 'center' : 'flex-end',
                                    paddingBottom: isMobile ? '0' : contentPadB,
                                }}>
                                    <div style={{
                                        maxWidth: 'var(--maxWidth)', margin: '0 auto',
                                        padding: !isDesktop ? '0 20px' : '0 24px',
                                        width: '100%',
                                        boxSizing: 'border-box'
                                    }}>
                                        <div style={{ maxWidth: !isDesktop ? '100%' : '680px', boxSizing: 'border-box' }}>
                                            {slide.heading && (
                                                <h1 style={{
                                                    fontFamily: 'var(--fontDisplay)',
                                                    fontSize: !isDesktop ? 'clamp(32px, 9vw, 52px)' : 'clamp(42px, 6vw, 80px)',
                                                    fontWeight: 900,
                                                    lineHeight: 1.0,
                                                    textTransform: 'uppercase',
                                                    color: '#ffffff',
                                                    letterSpacing: '-0.5px',
                                                    marginBottom: !isDesktop ? '12px' : '18px',
                                                    textShadow: '0 2px 16px rgba(0,0,0,0.5)',
                                                    textAlign: !isDesktop ? 'center' : 'left',
                                                }}>
                                                    {slide.heading}
                                                </h1>
                                            )}
                                            {slide.sub_text && (
                                                <p style={{
                                                    fontFamily: 'var(--fontBody)',
                                                    fontSize: !isDesktop ? '12px' : 'clamp(12px, 1.4vw, 16px)',
                                                    fontWeight: 500,
                                                    lineHeight: 1.6,
                                                    color: 'rgba(255,255,255,0.92)',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.8px',
                                                    marginBottom: !isDesktop ? '24px' : '32px',
                                                    maxWidth: !isDesktop ? '100%' : '560px',
                                                    textShadow: '0 1px 8px rgba(0,0,0,0.6)',
                                                    textAlign: !isDesktop ? 'center' : 'left',
                                                }}>
                                                    {slide.sub_text}
                                                </p>
                                            )}
                                            {slide.link && (
                                                <div style={{ display: 'flex', justifyContent: !isDesktop ? 'center' : 'flex-start' }}>
                                                    <a
                                                        href={slide.link}
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '10px',
                                                            padding: !isDesktop ? '12px 22px' : '14px 28px',
                                                            background: 'var(--blue)',
                                                            color: '#ffffff',
                                                            fontSize: !isDesktop ? '13px' : '14px',
                                                            fontWeight: 700,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px',
                                                            borderRadius: '4px',
                                                            textDecoration: 'none',
                                                            transition: 'transform 0.25s ease',
                                                        }}
                                                    >
                                                        Read More
                                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>

            {/* Slider Controls */}
            {slides.length > 1 && (
                <div style={{
                    position: 'absolute',
                    bottom: `${controlBottom}px`,
                    left: 0, right: 0, zIndex: 4,
                }}>
                    <div style={{
                        maxWidth: 'var(--maxWidth)', margin: '0 auto',
                        padding: isMobile ? '0 20px' : '0 24px',
                        display: 'flex', alignItems: 'center', gap: '10px',
                        justifyContent: isMobile ? 'center' : 'flex-start',
                    }}>
                        <button
                            onClick={() => goTo(currentIndex - 1)}
                            style={{
                                width: isMobile ? '30px' : '36px',
                                height: isMobile ? '30px' : '36px',
                                borderRadius: '50%',
                                border: '2px solid rgba(255,255,255,0.6)',
                                background: 'rgba(0,0,0,0.35)',
                                color: '#fff', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                            }}>
                            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg>
                        </button>
                        <button
                            onClick={() => goTo(currentIndex + 1)}
                            style={{
                                width: isMobile ? '30px' : '36px',
                                height: isMobile ? '30px' : '36px',
                                borderRadius: '50%',
                                border: '2px solid rgba(255,255,255,0.6)',
                                background: 'rgba(0,0,0,0.35)',
                                color: '#fff', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                            }}>
                            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
                        </button>

                        <div style={{ display: 'flex', gap: '6px' }}>
                            {slides.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => goTo(i)}
                                    style={{
                                        width: i === currentIndex ? '28px' : '18px',
                                        height: '3px',
                                        background: i === currentIndex ? '#fff' : 'rgba(255,255,255,0.35)',
                                        border: 'none', borderRadius: '2px',
                                        transition: 'all 0.25s', cursor: 'pointer',
                                    }}
                                />
                            ))}
                        </div>

                        {!isMobile && (
                            <div style={{
                                fontVariantNumeric: 'tabular-nums', fontSize: '13px',
                                fontWeight: 700, color: '#fff',
                                border: '2px solid rgba(255,255,255,0.6)',
                                borderRadius: '50%', width: '30px', height: '30px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: 'rgba(0,0,0,0.3)',
                            }}>
                                {currentIndex + 1}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
